import React, { useEffect, useState } from 'react'
import { BpButton } from '../../buttons/bp-button/BpButton'
import { useAiChat } from '../ai-provider/AiProvider'
import { SHORTCUT_LIST } from './consts/shortcut-list'
import { AiChatInput } from './ui/AiChatInput'
import { Controller } from 'react-hook-form'
import { IconAiUser } from '../../icons/IconAiUser'
import { AiMessage } from './ui/AiMessage'
import clsx from 'clsx'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { selectUser } from '../../../../app/store/reducers/AuthSlice'

const DotAnimation: React.FC = () => {
  const [dots, setDots] = useState('')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prevDots) => (prevDots.length < 3 ? prevDots + '.' : ''))
    }, 250)

    return () => clearInterval(interval)
  }, [])

  return <span>{dots}</span>
}

const Loader: React.FC = () => {
  const [text, setText] = useState('Thinking')

  useEffect(() => {
    const timeout = setTimeout(() => {
      setText('Hang on... almost done')
    }, 5000)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <div className='flex items-center gap-[8px] py-[8px]'>
      <div className={'h-[25px] w-[25px] rounded-full bg-[#E8EFFA] p-[5px] pl-[6px]'}>
        <div className={'w-[22px]'}>
          <IconAiUser color={'#00326D'} />
        </div>
      </div>
      <div className={'w-fit min-w-[50%] max-w-[85%] rounded-[8px] p-[8px] text-focus-500'}>
        <div className={'whitespace-pre-line text-[15px]'}>
          {text}
          <DotAnimation />
        </div>
      </div>
    </div>
  )
}

const FlowSwitcher: React.FC<{
  value: 'support' | 'analysis'
  onChange: (e: 'support' | 'analysis') => void
}> = ({ value, onChange }) => {
  const user = useAppSelector(selectUser)

  if (user.role !== 'admin' && !user.is_virtual_mode) {
    return null
  }

  return (
    <div className='flex rounded-[6px] p-[2px] shadow-outline shadow-focus/[0.65]'>
      <button
        className={clsx(
          'w-full rounded-[6px] py-[6px] transition-colors',
          value === 'support' ? 'bg-default/[0.08] text-default' : 'bg-transparent text-aqua-300',
        )}
        onClick={() => onChange('support')}
      >
        Support
      </button>
      <button
        className={clsx(
          'w-full rounded-[6px] py-[6px] transition-colors',
          value === 'analysis' ? 'bg-default/[0.08] text-default' : 'bg-transparent text-aqua-300',
        )}
        onClick={() => onChange('analysis')}
      >
        Analysis
      </button>
    </div>
  )
}

export const AiChat = () => {
  const { loading, flow, setFlow, messages, handleSendMessage, control, handleSubmit, chatRef } =
    useAiChat()

  return (
    <div className='max-h-full flex-1 overflow-hidden px-[24px] pt-[16px] pb-[8px]'>
      <div className='flex h-full flex-col'>
        <div
          ref={chatRef}
          className='scrollbar-hide mb-[16px] flex flex-1 flex-col gap-[8px] overflow-y-auto'
        >
          {messages.map((message, idx) => (
            <AiMessage key={idx} time={message.time} type={message.type}>
              {message.text}
            </AiMessage>
          ))}
          {loading && <Loader />}
          {messages.length === 1 && (
            <div className='flex flex-wrap gap-[8px] py-[8px] pl-[32px]'>
              {SHORTCUT_LIST.map((shortcut, idx) => (
                <BpButton
                  key={idx}
                  contentClassName={'font-[400]'}
                  size='sm'
                  theme='outline'
                  onClick={() => handleSendMessage({ message: shortcut.text })}
                >
                  {shortcut.value}
                </BpButton>
              ))}
            </div>
          )}
        </div>
        <div className={'mb-[14px]'}>
          <FlowSwitcher value={flow} onChange={setFlow} />
        </div>
        <form className='flex flex-col gap-[8px]' onSubmit={handleSubmit(handleSendMessage)}>
          <Controller
            render={({ field }) => (
              <AiChatInput
                {...field}
                autoSize={{ maxRows: 3 }}
                disabled={loading}
                placeholder='Ask a question...'
              />
            )}
            control={control}
            name={'message'}
            rules={{ required: true }}
          />
        </form>

        <p className={'pt-[8px] text-[12px] text-[#6684A7]'}>
          AI-generated response. Double-check for important decisions.
        </p>
      </div>
    </div>
  )
}
